function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useCallback, useEffect, useState, FC, ChangeEventHandler, CSSProperties, RefObject, MutableRefObject, ReactNode } from 'react';
import cn from 'classnames';
import { createSyntheticEvent } from '../../_internal_/utils/createSyntheticEvent';
import { useForkRef } from '../../hooks/useForkRef';
import { Menu } from '../Menu';
import { TextInput, ITextInputProps } from '../TextInput';
import { useListboxState, IUseListboxState, StateListbox, CollectionOption } from '../../hooks/useListboxState';
import { useUniqId } from '../../hooks/useUniqId';
require("./SelectDrawer.css");
var styles = {
  "Root": "SelectDrawer-module-Root_kA8",
  "ContentWrapper": "SelectDrawer-module-ContentWrapper_2Rk",
  "MenuWrapper": "SelectDrawer-module-MenuWrapper_2LR",
  "TextInput": "SelectDrawer-module-TextInput_kXP",
  "TextInputBox": "SelectDrawer-module-TextInputBox_3v_",
  "expanded": "SelectDrawer-module-expanded_2IF",
  "focused": "SelectDrawer-module-focused_1kX",
  "NoOptions": "SelectDrawer-module-NoOptions_1Ou",
  "Loading": "SelectDrawer-module-Loading_2Xt",
  "errorState": "SelectDrawer-module-errorState_ePP"
};
import { DrawerOverlay, DrawerOverlayPositions } from '../Drawer';
export const cnSelectDrawer = cn('SelectDrawer');
const emptyArray = [];
export const SelectDrawer = ({
  disableFilter = false,
  checkedOptions,
  className,
  controlRef,
  filterOptions,
  innerRef,
  label,
  loading,
  name,
  noOptionsResult = /*#__PURE__*/React.createElement("h4", null, "'\u041F\u043E \u0437\u0430\u043F\u0440\u043E\u0441\u0443 \u043D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E'"),
  hasClear = false,
  onBlur,
  onChange,
  onCheck,
  onFocus,
  options = emptyArray,
  state,
  style = {},
  value = '',
  hasIndependentLabel = false,
  contentSize = '90vh',
  position = DrawerOverlayPositions.bottom,
  isVisible = false,
  ...rest
}) => {
  const [allowFilter, setAllowFilter] = useState(!disableFilter);
  const {
    checkedEvent,
    checkedKeys,
    collectionOptions,
    interactiveKeys,
    onKeyDown,
    removeCheckedKey,
    selectedKey,
    ...listBoxState
  } = useListboxState({
    allowFilter,
    checkedOptions,
    filterOptions,
    options,
    query: value
  });
  const id = useUniqId('menu');
  const inputId = useUniqId('input');
  const _controlRef = useRef();
  const ownRef = useForkRef(_controlRef, controlRef);
  const setInputValue = useCallback((selectedKey, eventType) => {
    const input = _controlRef.current;
    if (!input || !onChange) return;
    input.value = selectedKey !== undefined && collectionOptions?.get(selectedKey) && eventType !== 'remove' ? collectionOptions?.get(selectedKey)?.label || '' : '';
    const event = new Event('change', {
      bubbles: true
    });
    input.dispatchEvent(event);
    const syntheticEvent = createSyntheticEvent(event);
    onChange(syntheticEvent);
  }, [collectionOptions, onChange]);
  useEffect(() => {
    // при управлении с клавиатуры временно подставляем в инпут значение,
    // но не вызываем событие, таким образом в родительском стейте продолжает храниться ранее отмеченное значение
    if (_controlRef.current) {
      _controlRef.current.value = selectedKey?.toString().length && collectionOptions.get(selectedKey)?.label || '';
    }
  }, [selectedKey, collectionOptions]);
  useEffect(() => {
    setAllowFilter(false);
    setInputValue(checkedEvent.key, checkedEvent.type);
    window.requestAnimationFrame(() => {
      setAllowFilter(!disableFilter);
    });
    onCheck && onCheck(checkedEvent.key, collectionOptions?.get(checkedEvent.key), checkedEvent.type);
    return () => {
      setAllowFilter(!disableFilter);
    };
  }, [checkedEvent, collectionOptions, onCheck, setInputValue, disableFilter]);
  useEffect(() => {
    if (isVisible) {
      _controlRef?.current?.focus();
    }
  }, [isVisible]);
  const handleBlur = useCallback(e => {
    if (onBlur) {
      onBlur(e);
    }
    setInputValue(checkedEvent.key, checkedEvent.type);
  }, [checkedEvent, onBlur, setInputValue]);
  const handleKeyDown = useCallback(e => {
    onKeyDown(e);
  }, [onKeyDown]);
  return /*#__PURE__*/React.createElement(DrawerOverlay, {
    className: cn(styles.Root, className),
    innerRef: innerRef,
    isExpanded: isVisible,
    style: {
      zIndex: 3,
      ...style
    },
    size: contentSize,
    position: position,
    onClose: handleBlur,
    keepMounted: true
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.ContentWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles[`${state}State`], {
      [styles.hasIndependentLabel]: hasIndependentLabel
    })
  }, /*#__PURE__*/React.createElement(TextInput, _extends({
    "aria-controls": id,
    "aria-expanded": isVisible,
    className: styles.TextInput,
    classNameBox: cn(styles.TextInputBox, styles.focused),
    hasClear: hasClear,
    controlRef: ownRef,
    id: inputId,
    name: name,
    onFocus: onFocus,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    onInput: onChange,
    label: label,
    role: "combobox",
    state: state,
    value: value,
    hasIndependentLabel: hasIndependentLabel,
    variant: "default"
  }, rest))), Boolean(interactiveKeys.length) && options.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: styles.MenuWrapper
  }, /*#__PURE__*/React.createElement(Menu, _extends({
    id: id,
    selectedKey: selectedKey,
    checkedKeys: checkedKeys,
    collectionOptions: collectionOptions
  }, listBoxState))), !interactiveKeys.length && options.length > 0 && allowFilter && /*#__PURE__*/React.createElement("div", {
    className: styles.NoOptions
  }, noOptionsResult), loading && options.length === 0 && /*#__PURE__*/React.createElement("div", {
    className: styles.Loading
  }, "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430...")));
};
if (process.env.NODE_ENV !== 'production') {
  SelectDrawer.displayName = 'SelectDrawer';
}